// src/components/admin/PlanForm.jsx
import React, { useState, useEffect } from 'react';
import AppSelectorDrawer from './AppSelectorDrawer';
import { X } from 'lucide-react';

const API = process.env.REACT_APP_API_BASE;

const PlanForm = ({ apps = [], industries = [], editingPlan, onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    name: '',
    monthlyPrice: '',
    yearlyPrice: '',
    requestLimit: '',
    tokenLimit: '',
    apps: [],
    prioritySupport: false,
    multiProvider: false,
    seats: '1',
    country: '',
    industryId: '',
    showOnSignup: true
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [countries, setCountries] = useState([]);


  useEffect(() => {
    if (editingPlan) {
      setForm({
        name: editingPlan.name || '',
        monthlyPrice: editingPlan.monthlyPrice || '',
        yearlyPrice: editingPlan.yearlyPrice || '',
        requestLimit: editingPlan.requestLimit || '',
        tokenLimit: editingPlan.tokenLimit || '',
        apps: editingPlan.apps || [],
        prioritySupport: editingPlan.prioritySupport || false,
        multiProvider: editingPlan.multiProvider || false,
        seats: editingPlan.seats || '1',
        country: editingPlan.country || '',
        industryId: editingPlan.industryId || '',
        showOnSignup: editingPlan.showOnSignup ?? true
      });
    }
  }, [editingPlan]);

  useEffect(() => {
    fetch(`${API}/api/countries`)
      .then(res => res.json())
      .then(setCountries)
      .catch(() => console.error('Failed to load countries'));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setForm({ ...form, [name]: newValue });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.monthlyPrice || !form.yearlyPrice || !form.country || !form.industryId) return alert('Name, Prices, and Country are required');
    onSubmit(form);
  };

  return (
    <div className="bg-white border p-4 rounded-md shadow space-y-4">
      <h3 className="text-lg font-semibold">{editingPlan ? 'Edit Plan' : 'Create Plan'}</h3>

      <div className="grid grid-cols-2 gap-4">
        <input
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="Plan Name"
          className="border px-3 py-2 rounded w-full"
        />
 <select
          name="country"
          value={form.country}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
          required
        >
          <option value="">Select Country</option>
          {countries.map((c) => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>
        <select
  name="industryId"
  value={form.industryId}
  onChange={handleChange}
  required
  className="w-full border px-3 py-2 rounded"
>
  <option value="">Select Industry</option>
  {industries.map(i => (
    <option key={i._id} value={i._id}>
      {i.displayName}
    </option>
  ))}
</select>

        <input
          name="monthlyPrice"
          value={form.monthlyPrice}
          onChange={handleChange}
          placeholder="Monthly Price (USD)"
          type="number"
          min={0}
          className="border px-3 py-2 rounded w-full"
        />

        <input
          name="yearlyPrice"
          value={form.yearlyPrice}
          onChange={handleChange}
          placeholder="Yearly Price (USD)"
          type="number"
          min={0}
          className="border px-3 py-2 rounded w-full"
        />

        <input
          name="requestLimit"
          value={form.requestLimit}
          onChange={handleChange}
          placeholder="Request Limit (Monthly)"
          type="number"
          min={0}
          className="border px-3 py-2 rounded w-full"
        />

        <input
          name="tokenLimit"
          value={form.tokenLimit}
          onChange={handleChange}
          placeholder="Token Limit (Monthly)"
          type="number"
          min={0}
          className="border px-3 py-2 rounded w-full"
        />

        <input
          name="seats"
          value={form.seats}
          onChange={handleChange}
          placeholder="Team Seats (optional)"
          type="number"
          min={0}
          className="border px-3 py-2 rounded w-full"
        />

       
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            name="prioritySupport"
            checked={form.prioritySupport}
            onChange={handleChange}
          />
          Priority Support
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            name="multiProvider"
            checked={form.multiProvider}
            onChange={handleChange}
          />
          Multi-Provider Models
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            name="showOnSignup"
            checked={form.showOnSignup}
            onChange={handleChange}
          />
          Show on Signup
        </label>
      </div>

      <div className="space-y-2">
        <label className="font-medium text-sm">Apps</label>
        <div className="flex flex-wrap gap-2">
          {form.apps.includes('all') ? (
            <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-sm">All Apps</span>
          ) : (
            form.apps.map(appId => {
              const app = apps.find(a => a._id === appId);
              return (
                <span key={appId} className="bg-gray-100 text-sm px-2 py-1 rounded flex items-center gap-1">
                  {app?.name || 'Unknown'}
                  <button
                    onClick={() => setForm({ ...form, apps: form.apps.filter(id => id !== appId) })}
                    className="text-gray-500 hover:text-red-500"
                  >
                    <X size={12} />
                  </button>
                </span>
              );
            })
          )}
        </div>
        <button
          onClick={() => setDrawerOpen(true)}
          className="text-blue-600 text-sm underline"
        >
          Select Apps
        </button>
      </div>

      <AppSelectorDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        apps={apps}
        selectedAppIds={form.apps}
        onSave={(selected) => {
          setForm({ ...form, apps: selected });
          setDrawerOpen(false);
        }}
      />

      <div className="flex gap-2">
        <button onClick={handleSubmit} className="bg-blue-600 text-white px-4 py-2 rounded">
          {editingPlan ? 'Update Plan' : 'Create Plan'}
        </button>
        {editingPlan && (
          <button onClick={onCancel} className="text-sm underline text-gray-600">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default PlanForm;
